/**
 * avatars/animals/animal-parts.js
 * Các hàm helper tái sử dụng dựng các chi tiết cơ thể: Mắt, Má hồng, Tai, Sừng, Mõm, Mỏ, Bờm, Đuôi, Chân màng
 */

import * as THREE from 'three';
import { getColorLuminance, shadeColor } from '../../core/color.js';
import { generateAnimalTexture } from '../animal-presets.js';

/**
 * 1. Dựng cặp Mắt 3D Chibi và Điểm phản quang (Tỉ lệ chuẩn P-7 & Tương phản đồng tử P-3)
 */
export function buildEyes(headGroup, morph, variant, mats, disposables, isAvian = false) {
  const m = morph;
  const v = variant;

  const eyeW = m.head.sizeX * 0.16;
  const eyeH = m.head.sizeY * 0.2;
  const eyeD = 0.018;
  const hlSize = eyeH * 0.45;

  const eyeSpacing = isAvian ? m.head.sizeX * 0.34 : m.head.sizeX * 0.3;
  const isHighSnout = !isAvian && m.snout && m.head && m.snout.height / m.head.sizeY > 0.4;
  const eyeY = isAvian ? m.head.sizeY * 0.44 : isHighSnout ? m.head.sizeY * 0.56 : m.head.sizeY * 0.46;
  const eyeZ = isAvian ? m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + 0.005 : m.head.sizeZ + 0.008;

  const eyeGeo = new THREE.BoxGeometry(eyeW, eyeH, eyeD);
  const eyeL = new THREE.Mesh(eyeGeo, mats.eyeMat);
  eyeL.position.set(-eyeSpacing, eyeY, eyeZ);
  const eyeR = new THREE.Mesh(eyeGeo, mats.eyeMat);
  eyeR.position.set(eyeSpacing, eyeY, eyeZ);
  headGroup.add(eyeL, eyeR);
  disposables.push(eyeGeo);

  // Đồng tử cho mắt sáng & viền tương phản khi nền tối (P-3 & F-1)
  const eyeLum = getColorLuminance(v.eyeColor || '#0f172a');
  const baseLum = getColorLuminance(v.baseColor || '#ffffff');

  if (eyeLum > 0.6) {
    const pupilGeo = new THREE.BoxGeometry(eyeW * 0.6, eyeH * 0.6, 0.005);
    const pupilMat = new THREE.MeshStandardMaterial({ color: '#0f172a', roughness: 0.3 });
    const pupilZ = eyeZ + eyeD / 2 + 0.003;
    const pupilL = new THREE.Mesh(pupilGeo, pupilMat);
    pupilL.name = 'PupilL';
    pupilL.position.set(-eyeSpacing, eyeY, pupilZ);
    const pupilR = new THREE.Mesh(pupilGeo, pupilMat);
    pupilR.name = 'PupilR';
    pupilR.position.set(eyeSpacing, eyeY, pupilZ);
    headGroup.add(pupilL, pupilR);
    disposables.push(pupilGeo, pupilMat);
  } else if (baseLum < 0.2 && eyeLum <= 0.6) {
    const rimGeo = new THREE.BoxGeometry(eyeW * 1.25, eyeH * 1.25, 0.01);
    const rimMat = new THREE.MeshStandardMaterial({ color: '#e2e8f0', roughness: 0.5 });
    const rimL = new THREE.Mesh(rimGeo, rimMat);
    rimL.position.set(-eyeSpacing, eyeY, eyeZ - 0.005);
    const rimR = new THREE.Mesh(rimGeo, rimMat);
    rimR.position.set(eyeSpacing, eyeY, eyeZ - 0.005);
    headGroup.add(rimL, rimR);
    disposables.push(rimGeo, rimMat);
  }

  // Điểm catchlight phản quang lanh lợi (đặt nổi phía trước đồng tử, không xuyên vào mắt)
  const hlGeo = new THREE.BoxGeometry(hlSize, hlSize, 0.004);
  const hlOffsetX = hlSize * 0.5;
  const hlOffsetY = hlSize * 0.45;
  const hlZ = eyeZ + eyeD / 2 + 0.006;
  const hlL = new THREE.Mesh(hlGeo, mats.eyeHighlightMat);
  hlL.position.set(-eyeSpacing + hlOffsetX, eyeY + hlOffsetY, hlZ);
  const hlR = new THREE.Mesh(hlGeo, mats.eyeHighlightMat);
  hlR.position.set(eyeSpacing - hlOffsetX, eyeY + hlOffsetY, hlZ);
  headGroup.add(hlL, hlR);
  disposables.push(hlGeo);
}

/**
 * 2. Dựng Má hồng Chibi (Kawaii Blush)
 */
export function buildBlush(headGroup, morph, mats, disposables, isAvian = false) {
  const m = morph;
  const eyeSpacing = isAvian ? m.head.sizeX * 0.34 : m.head.sizeX * 0.3;
  const isHighSnout = !isAvian && m.snout && m.head && m.snout.height / m.head.sizeY > 0.4;
  const eyeY = isAvian ? m.head.sizeY * 0.44 : isHighSnout ? m.head.sizeY * 0.56 : m.head.sizeY * 0.46;
  const eyeZ = isAvian ? m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + 0.005 : m.head.sizeZ + 0.008;

  const blushW = isAvian ? 0.05 : 0.06;
  const blushH = isAvian ? 0.02 : 0.025;
  const blushGeo = new THREE.BoxGeometry(blushW, blushH, 0.014);

  const blushL = new THREE.Mesh(blushGeo, mats.blushMat);
  blushL.position.set(-eyeSpacing - blushW * 0.5, eyeY - eyeH_ratio(m) * 0.8, eyeZ + 0.002);
  const blushR = new THREE.Mesh(blushGeo, mats.blushMat);
  blushR.position.set(eyeSpacing + blushW * 0.5, eyeY - eyeH_ratio(m) * 0.8, eyeZ + 0.002);
  headGroup.add(blushL, blushR);
  disposables.push(blushGeo);
}

function eyeH_ratio(morph) {
  return morph.head.sizeY * 0.2;
}

/**
 * 3. Dựng Tai (Ears) cho Động vật 4 chân
 */
export function buildEars(headGroup, morph, mats, disposables, parts, variant) {
  const m = morph;
  const earW = m.ears.width;
  const earH = m.ears.height;
  const earD = m.ears.depth;
  const earGeo = new THREE.BoxGeometry(earW, earH, earD);
  disposables.push(earGeo);

  // Tai cừu dùng skinColor
  const earMat = variant.skinColor ? mats.legMat : mats.flankMat;

  const leftEar = new THREE.Group();
  const rightEar = new THREE.Group();
  const earMeshL = new THREE.Mesh(earGeo, earMat);
  const earMeshR = new THREE.Mesh(earGeo, earMat);

  const topHeadY = m.head.sizeY * 0.3 + m.head.sizeY / 2;
  const midHeadZ = m.head.sizeZ * 0.45;

  if (m.ears.type === 'pointy' || m.ears.type === 'pointy_tall') {
    leftEar.position.set(-m.head.sizeX * 0.36, topHeadY, midHeadZ);
    rightEar.position.set(m.head.sizeX * 0.36, topHeadY, midHeadZ);
    earMeshL.position.y = earH / 2;
    earMeshR.position.y = earH / 2;
    earMeshL.rotation.z = -0.15;
    earMeshR.rotation.z = 0.15;
  } else if (m.ears.type === 'side' || m.ears.type === 'side_droop') {
    leftEar.position.set(-m.head.sizeX * 0.52, m.head.sizeY * 0.35, midHeadZ);
    rightEar.position.set(m.head.sizeX * 0.52, m.head.sizeY * 0.35, midHeadZ);
    earMeshL.position.x = -earW / 2;
    earMeshR.position.x = earW / 2;
    earMeshL.rotation.z = -0.3;
    earMeshR.rotation.z = 0.3;
  } else {
    // floppy / floppy_front / droop
    leftEar.position.set(-m.head.sizeX * 0.48, topHeadY - 0.02, midHeadZ);
    rightEar.position.set(m.head.sizeX * 0.48, topHeadY - 0.02, midHeadZ);
    earMeshL.position.y = -earH / 2;
    earMeshR.position.y = -earH / 2;
    earMeshL.rotation.z = 0.2;
    earMeshR.rotation.z = -0.2;
  }

  leftEar.add(earMeshL);
  rightEar.add(earMeshR);
  headGroup.add(leftEar, rightEar);
  parts.leftEar = leftEar;
  parts.rightEar = rightEar;
}

/**
 * 4. Dựng Sừng cho Bò (Horns)
 */
export function buildHorns(headGroup, morph, mats, disposables) {
  const m = morph;
  if (!m.horns || !m.horns.hasHorns) return;

  const hornGeo = new THREE.BoxGeometry(m.horns.thickness, m.horns.length, m.horns.thickness);
  const hornL = new THREE.Mesh(hornGeo, mats.hornMat);
  const topHeadY = m.head.sizeY * 0.3 + m.head.sizeY / 2;
  const midHeadZ = m.head.sizeZ * 0.45;

  hornL.position.set(-m.head.sizeX * 0.42, topHeadY + m.horns.length * 0.3, midHeadZ);
  hornL.rotation.z = -0.4;
  hornL.rotation.x = -0.2;

  const hornR = new THREE.Mesh(hornGeo, mats.hornMat);
  hornR.position.set(m.head.sizeX * 0.42, topHeadY + m.horns.length * 0.3, midHeadZ);
  hornR.rotation.z = 0.4;
  hornR.rotation.x = -0.2;

  headGroup.add(hornL, hornR);
  disposables.push(hornGeo);
}

/**
 * 5. Dựng Đuôi (Tail) đa dạng kiểu (P-6: hanging_tuft, horse_bushy, short_stub, cat_L, wagging_up, pig_curly)
 */
export function buildTail(bodyGroup, morph, variant, mats, disposables, parts) {
  const m = morph;
  const v = variant;
  const tailType = m.tail?.type || 'wagging_up';

  const tailGroup = new THREE.Group();
  tailGroup.name = 'TailGroup';
  tailGroup.position.set(0, m.body.height * 0.2, -m.body.length / 2);
  bodyGroup.add(tailGroup);
  parts.tail = tailGroup;

  if (tailType === 'hanging_tuft') {
    // Đuôi bò: Thanh mảnh rủ xuống + chùm lông cuối cube
    const stemLen = (m.tail.length || 0.22) * 0.8;
    const stemThick = m.tail.thickness || 0.045;
    const stemGeo = new THREE.BoxGeometry(stemThick, stemLen, stemThick);
    const stemMesh = new THREE.Mesh(stemGeo, mats.flankMat);
    stemMesh.position.set(0, -stemLen * 0.4, -stemThick * 0.4);
    stemMesh.rotation.x = m.tail.angle || -0.15;
    tailGroup.add(stemMesh);

    const tuftColor = v.patternColor || shadeColor(v.baseColor, -30);
    const tuftMat = new THREE.MeshStandardMaterial({ color: tuftColor, roughness: 0.9 });
    const tuftSize = stemThick * 2.2;
    const tuftGeo = new THREE.BoxGeometry(tuftSize, tuftSize, tuftSize);
    const tuftMesh = new THREE.Mesh(tuftGeo, tuftMat);
    tuftMesh.position.set(0, -stemLen * 0.85, -stemThick * 0.5);
    tailGroup.add(tuftMesh);
    disposables.push(stemGeo, tuftGeo, tuftMat);
  } else if (tailType === 'horse_bushy') {
    // Đuôi ngựa: 2 đoạn dày dần rủ xuống xoè nhẹ
    const len1 = (m.tail.length || 0.26) * 0.55;
    const len2 = (m.tail.length || 0.26) * 0.65;
    const thick = m.tail.thickness || 0.06;

    const seg1Geo = new THREE.BoxGeometry(thick * 1.1, len1, thick * 1.1);
    const seg1 = new THREE.Mesh(seg1Geo, mats.maneMat);
    seg1.position.set(0, -len1 * 0.4, -thick * 0.5);
    seg1.rotation.x = -0.25;
    tailGroup.add(seg1);

    const seg2Geo = new THREE.BoxGeometry(thick * 1.4, len2, thick * 1.4);
    const seg2 = new THREE.Mesh(seg2Geo, mats.maneMat);
    seg2.position.set(0, -len1 * 0.8 - len2 * 0.35, -thick * 1.1);
    seg2.rotation.x = -0.1;
    tailGroup.add(seg2);
    disposables.push(seg1Geo, seg2Geo);
  } else if (tailType === 'short_stub') {
    // Đuôi cừu: Cube ngắn bọc len
    const stubSize = (m.tail.thickness || 0.05) * 1.4;
    const stubGeo = new THREE.BoxGeometry(stubSize, stubSize, (m.tail.length || 0.08) * 1.2);
    const woolCanvas = generateAnimalTexture('wool_fluff', { baseColor: v.baseColor });
    let stubMat = mats.flankMat;
    if (woolCanvas) {
      const woolTex = new THREE.CanvasTexture(woolCanvas);
      woolTex.colorSpace = THREE.SRGBColorSpace;
      stubMat = new THREE.MeshStandardMaterial({ map: woolTex, roughness: 0.95 });
      disposables.push(woolTex, stubMat);
    }
    const stubMesh = new THREE.Mesh(stubGeo, stubMat);
    stubMesh.position.set(0, 0, -stubSize * 0.5);
    stubMesh.rotation.x = m.tail.angle || -0.1;
    tailGroup.add(stubMesh);
    disposables.push(stubGeo);
  } else if (tailType === 'cat_L') {
    // Đuôi chữ L dựng đứng của Mèo
    const baseLen = m.tail.length * 0.45;
    const vertLen = m.tail.length * 0.75;
    const baseGeo = new THREE.BoxGeometry(m.tail.thickness, m.tail.thickness, baseLen);
    const baseMesh = new THREE.Mesh(baseGeo, mats.flankMat);
    baseMesh.position.set(0, 0, -baseLen / 2);
    tailGroup.add(baseMesh);

    const vertGeo = new THREE.BoxGeometry(m.tail.thickness, vertLen, m.tail.thickness);
    const vertMesh = new THREE.Mesh(vertGeo, mats.flankMat);
    vertMesh.position.set(0, vertLen / 2 - m.tail.thickness / 2, -baseLen + m.tail.thickness / 2);
    tailGroup.add(vertMesh);
    disposables.push(baseGeo, vertGeo);
  } else if (tailType === 'pig_curly') {
    // Đuôi heo nhỏ xoắn
    const tailGeo = new THREE.BoxGeometry(m.tail.thickness, m.tail.length, m.tail.thickness);
    const tailMesh = new THREE.Mesh(tailGeo, mats.flankMat);
    tailMesh.position.set(0, m.tail.length * 0.35, -m.tail.thickness);
    tailMesh.rotation.x = m.tail.angle || 0.4;
    tailMesh.rotation.z = 0.35;
    tailGroup.add(tailMesh);
    disposables.push(tailGeo);
  } else {
    // wagging_up (Chó vểnh lên)
    const tailGeo = new THREE.BoxGeometry(m.tail.thickness, m.tail.length, m.tail.thickness);
    const tailMesh = new THREE.Mesh(tailGeo, mats.flankMat);
    tailMesh.position.set(0, m.tail.length * 0.4, -m.tail.thickness * 0.5);
    tailMesh.rotation.x = m.tail.angle || 0.7;
    tailGroup.add(tailMesh);
    disposables.push(tailGeo);
  }
}

/**
 * 6. Dựng Mỏ gia cầm (P-6: flat_paddle vịt & pointed gà)
 */
export function buildAvianBeak(headGroup, morph, mats, disposables) {
  const m = morph;
  const isPaddle = m.beak.type === 'flat_paddle';

  if (isPaddle) {
    // Vịt: Mỏ dẹt to bè + viền mỏ trên dày hơn mỏ dưới
    const upperGeo = new THREE.BoxGeometry(m.beak.width * 1.1, m.beak.height * 0.6, m.beak.length);
    const upperMesh = new THREE.Mesh(upperGeo, mats.beakMat);
    upperMesh.position.set(0, m.head.sizeY * 0.26, m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + m.beak.length / 2);

    const lowerGeo = new THREE.BoxGeometry(m.beak.width * 0.95, m.beak.height * 0.4, m.beak.length * 0.9);
    const lowerMesh = new THREE.Mesh(lowerGeo, mats.beakMat);
    lowerMesh.position.set(
      0,
      m.head.sizeY * 0.26 - m.beak.height * 0.45,
      m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + (m.beak.length * 0.9) / 2
    );

    headGroup.add(upperMesh, lowerMesh);
    disposables.push(upperGeo, lowerGeo);
  } else {
    // Gà: Mỏ nhọn 2 tầng nhỏ dần
    const baseGeo = new THREE.BoxGeometry(m.beak.width, m.beak.height, m.beak.length * 0.6);
    const baseMesh = new THREE.Mesh(baseGeo, mats.beakMat);
    baseMesh.position.set(0, m.head.sizeY * 0.24, m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + (m.beak.length * 0.6) / 2);

    const tipGeo = new THREE.BoxGeometry(m.beak.width * 0.65, m.beak.height * 0.65, m.beak.length * 0.4);
    const tipMesh = new THREE.Mesh(tipGeo, mats.beakMat);
    tipMesh.position.set(
      0,
      m.head.sizeY * 0.24,
      m.head.sizeZ * 0.25 + m.head.sizeZ / 2 + m.beak.length * 0.6 + (m.beak.length * 0.4) / 2
    );

    headGroup.add(baseMesh, tipMesh);
    disposables.push(baseGeo, tipGeo);
  }
}

/**
 * 7. Dựng Bàn chân gia cầm (P-6: isWebbed vịt xoè quạt 3 khối vs gà)
 */
export function buildAvianFoot(legJoint, legH, morph, mats, disposables) {
  const m = morph;
  const isWebbed = !!m.legs.isWebbed;

  if (isWebbed) {
    // Chân vịt có màng: Bàn chân hình quạt 3 khối xòe (giữa + 2 bên lệch góc ±0.35 rad)
    const footLen = m.legs.footLength || 0.1;
    const toeW = m.legs.width * 0.6;
    const toeGeo = new THREE.BoxGeometry(toeW, 0.016, footLen);
    disposables.push(toeGeo);

    // Khối giữa
    const midToe = new THREE.Mesh(toeGeo, mats.legMat);
    midToe.position.set(0, -legH + 0.008, footLen * 0.4);
    legJoint.add(midToe);

    // Khối ngón trái lệch -0.35 rad
    const leftToe = new THREE.Mesh(toeGeo, mats.legMat);
    leftToe.position.set(-toeW * 0.75, -legH + 0.008, footLen * 0.38);
    leftToe.rotation.y = -0.35;
    legJoint.add(leftToe);

    // Khối ngón phải lệch +0.35 rad
    const rightToe = new THREE.Mesh(toeGeo, mats.legMat);
    rightToe.position.set(toeW * 0.75, -legH + 0.008, footLen * 0.38);
    rightToe.rotation.y = 0.35;
    legJoint.add(rightToe);
  } else {
    // Bàn chân gà thông thường
    const footGeo = new THREE.BoxGeometry(m.legs.width * 1.8, 0.02, m.legs.footLength || 0.08);
    const footMesh = new THREE.Mesh(footGeo, mats.legMat);
    footMesh.position.set(0, -legH + 0.01, (m.legs.footLength || 0.08) * 0.35);
    legJoint.add(footMesh);
    disposables.push(footGeo);
  }
}
